<template>
    <div class="settings-summary" :class="{['smaller-text']: smallerText }">
		<div class="settings-content" v-if="isBaseGame">
            <span class="setting-readonly" >
                Base Game
            </span>
		</div>
        <div class="settings-content settings-flex-wrap" v-else>
            <span class="setting-readonly" v-if="gameState.nobility">
				<fa-icon
					icon="crown"
					class="icon expansion-icon nobility"
				/>
				Nobility
            </span>
            <span class="setting-readonly" v-if="gameState.siege">
				<icon
					icon="fort"
					class="icon expansion-icon siege"
				/>
                Siege
            </span>
            <span class="setting-readonly" v-if="gameState.nightfall">
				<icon
					icon="poison"
					class="icon expansion-icon nightfall"
				/>
                Nightfall
            </span>
			<span class="setting-readonly" v-if="gameState.shock">
				<ShockIcon class="icon expansion-icon shock-tactics" />
                Shock Tactics
            </span>
			<span class="setting-readonly" v-if="gameState.champions">
				<WingedHelmetIcon class="icon expansion-icon champions" />
                Champions
            </span>
            <span class="setting-readonly" v-if="gameState.mastery">
				<fa-icon
					icon="feather"
					class="icon expansion-icon mastery"
				/>
                Mastery
            </span>
			<span class="setting-readonly" v-if="gameState.highSeas">
				<icon
					icon="ship"
					class="icon expansion-icon high-seas"
				/>
                High Seas
            </span>
		</div>
		<div class="settings-content settings-flex-wrap">
			<span class="setting-readonly smaller-text">
				{{ renderDraftLabel(gameState.draftType) }}
			</span>
			<span class="setting-readonly smaller-text" v-if="gameState.choosePlayOrder && gameState.draftType !== 'random'">
				Choose play order
			</span>
		</div>
		<div class="settings-content">
			<div class="setting-readonly custom-units-label" v-if="gameState.customUnits">
				Custom units
			</div>
			<div class="custom-unit-types" v-if="gameState.customUnits">
				<unit-types-selector
					:unitTypes="gameState.customUnitTypes"
					:selectedUnitTypes="gameState.customUnitTypes"
					:nobilityExpansion="gameState.nobility"
					:siegeExpansion="gameState.siege"
					:onChange="() => {}"
					:readonly="true"
					:showRemarks="true"
				/>
			</div>
			<div class="setting-readonly alt-units-label" v-if="altUnits"
				:class="{['no-margin']: gameState.customUnits}"
			>
				Alternative units
			</div>
			<div class="setting-readonly-alt-units" v-if="altUnits">
				<span class="setting-readonly readonly-alt-unit-type" v-if="gameState.altFootman">
					<unit-toggle
						:unitType="gameState.altFootman ? UNIT_TYPE_VANGUARD : UNIT_TYPE_FOOTMAN"
						:checked="gameState.altFootman"
						:callback="null"
					/>
				</span>
				<span class="setting-readonly readonly-alt-unit-type" v-if="gameState.altLancer">
					<unit-toggle
						:unitType="gameState.altLancer ? UNIT_TYPE_HEAVY_CAVALRY : UNIT_TYPE_LANCER"
						:checked="gameState.altLancer"
						:callback="null"
					/>
				</span>
				<span class="setting-readonly readonly-alt-unit-type" v-if="gameState.altMarshal">
					<unit-toggle
						:unitType="gameState.altMarshal ? UNIT_TYPE_WARLORD : UNIT_TYPE_MARSHAL"
						:checked="gameState.altMarshal"
						:callback="null"
					/>
				</span>
			</div>
		</div>
		<div class="settings-content">
			<span class="setting-readonly" v-if="gameState.twoPlayersFullBoard">
				Jackson Variant
			</span>
		</div>
	</div>
</template>
<script>
import { IDraftType } from '../Game/Game-types';
import {
    UNIT_TYPE_FOOTMAN,
    UNIT_TYPE_HEAVY_CAVALRY,
    UNIT_TYPE_LANCER,
    UNIT_TYPE_MARSHAL,
    UNIT_TYPE_ROYAL_GUARD,
    UNIT_TYPE_VANGUARD,
    UNIT_TYPE_WARLORD,
} from '../Unit/Unit-types';
import WingedHelmetIcon from '../../assets/winged_helmet.svg';
import ShockIcon from '../../assets/shock_icon.svg';

export default {
    name: 'SettingsSummary',
    props: ['smallerText', 'gameState'],
	components: {
		WingedHelmetIcon,
		ShockIcon,
	},
    data() {
        return {
            ['UNIT_TYPE_FOOTMAN']: UNIT_TYPE_FOOTMAN,
            ['UNIT_TYPE_HEAVY_CAVALRY']: UNIT_TYPE_HEAVY_CAVALRY,
            ['UNIT_TYPE_LANCER']: UNIT_TYPE_LANCER,
            ['UNIT_TYPE_MARSHAL']: UNIT_TYPE_MARSHAL,
            ['UNIT_TYPE_ROYAL_GUARD']: UNIT_TYPE_ROYAL_GUARD,
            ['UNIT_TYPE_VANGUARD']: UNIT_TYPE_VANGUARD,
            ['UNIT_TYPE_WARLORD']: UNIT_TYPE_WARLORD,
        };
    },
	computed: {
		isBaseGame() {
			const gameState = this.gameState;

			return !gameState.nobility &&
				!gameState.siege &&
				!gameState.nightfall &&
				!gameState.shock &&
				!gameState.mastery &&
				!gameState.highSeas &&
				!gameState.champions;
		},
		altUnits() {
			return this.gameState.altFootman ||
				this.gameState.altLancer ||
				this.gameState.altMarshal;
		}
	},
    methods: {
        renderDraftLabel(draftType) {
            let draftLabel;

            switch (draftType) {
                case IDraftType.DRAFT:
                    draftLabel = 'Draft Units';
                    break;
                case IDraftType.ELIMINATION_DRAFT:
                    draftLabel = 'Elimination Draft';
                    break;
                default:
                    draftLabel = 'Random Units';
            }

            return draftLabel;
        },
    },
};
</script>
<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .settings-summary {
		margin: 0 28px 20px;

		.settings-title {
			text-align: center;
			font-weight: bold;
		}

		.settings-content {
			margin-bottom: 10px;
		}

        .settings-title {
            font-size: .9em;
            margin-bottom: 5px;
        }

        .setting-readonly {
            font-size: .8em;
            padding: 4px 10px;
            display: inline-block;
            color: $dark-gray;
            border: 2px solid $extra-light-gray;
            background: $extra-light-gray;
            border-radius: 4px;

			&.smaller-text {
				font-size: .8rem;
			}

            &.custom-units-label,
            &.alt-units-label {
                display: block;
            }
        }

		.setting-readonly-alt-units {
			display: flex;
			justify-content: center;
			margin-top: 10px;
			gap: 10px;
		}

		.settings-flex-wrap {
			display: flex;
			flex-wrap: wrap;
			justify-content: center;
			gap: 10px;

			.setting-readonly {
				box-sizing: border-box;
				flex: 0 0 calc(50% - 5px);
			}
		}

        .alt-units-label {
            margin-top: 10px;

            &.no-margin {
                margin-top: 0;
            }
        }

        .custom-unit-types {
			margin-top: 10px;

            .custom-unit-type-wrapper {
                cursor: default;
            }
        }

		.expansion-icon {
			width: 12.5px;
			height: auto;
			max-height: 12.5px;
			color: $dark-blue;
			fill: $dark-blue;
			margin-right: 3px;

			&.nightfall {
				margin-bottom: -1.5px;
			}


			&.champions {
				width: 16px;
			}
		}
    }
</style>
